if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      selectedPackValue = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      onVariantKeydown = this.handleVariantKeydown.bind(this);
      abortController = undefined;
      quantityRulesAbortController = undefined;
      variantRequestId = 0;
      quantityRulesRequestId = 0;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      productSwapUtilityInitialized = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.syncPackQuantityState();
        this.updateOptionAvailability(this.getSelectedOptionNames());
        this.variantSelectors?.addEventListener(
          "keydown",
          this.onVariantKeydown
        );
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      syncPackQuantityState() {
        if (!this.quantityInput) return;

        const selectedPack = this.querySelector(
          '[data-variant-option-role="saver-pack"] input[type="radio"]:checked'
        );
        if (!selectedPack) return;

        const packChanged =
          this.selectedPackValue !== undefined &&
          this.selectedPackValue !== selectedPack.value;
        const isSinglePiece = selectedPack.dataset.singlePiece === "true";

        if (!isSinglePiece || packChanged) this.resetPackQuantity();

        this.dataset.singlePieceSelected = isSinglePiece ? "true" : "false";
        if (this.quantityForm) {
          this.syncPurchaseRowHeight();
          this.quantityForm.toggleAttribute("hidden", !isSinglePiece);
          this.quantityForm.toggleAttribute("inert", !isSinglePiece);
          if (isSinglePiece) {
            this.quantityForm.removeAttribute("aria-hidden");
            this.syncPurchaseRowHeight();
          } else {
            this.quantityForm.setAttribute("aria-hidden", "true");
          }
        }

        this.selectedPackValue = selectedPack.value;
      }

      syncPurchaseRowHeight() {
        if (!this.quantityForm || this.quantityForm.hidden) return;

        const purchase = this.quantityForm.closest(".product__purchase");
        const quantityHeight = this.quantityForm.getBoundingClientRect().height;
        if (!purchase || quantityHeight <= 0) return;

        purchase.style.setProperty(
          "--product-purchase-control-row-height",
          `${quantityHeight}px`
        );
      }

      resetPackQuantity() {
        if (!this.quantityInput || this.quantityInput.value === "1") return;

        this.quantityInput.value = "1";
        this.quantityInput.dispatchEvent(
          new Event("change", { bubbles: true })
        );
      }

      disconnectedCallback() {
        this.variantRequestId += 1;
        this.quantityRulesRequestId += 1;
        this.abortController?.abort();
        this.quantityRulesAbortController?.abort();
        this.variantSelectors?.removeEventListener(
          "keydown",
          this.onVariantKeydown
        );
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        if (this.productSwapUtilityInitialized) return;
        this.productSwapUtilityInitialized = true;
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.syncPackQuantityState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        const selectedOptionNames = this.getSelectedOptionNames();
        const shouldRestoreFocus =
          shouldSwapProduct &&
          this.variantSelectors?.contains(document.activeElement);

        this.resetProductFormState();

        if (shouldSwapProduct) {
          this.productForm?.toggleSubmitButton(true);
        } else {
          this.updateOptionAvailability(selectedOptionNames);
          const preview = this.getVariantPreview(selectedOptionNames);
          if (preview) {
            this.applyVariantPreview(preview, productUrl);
          } else {
            this.updateURL(productUrl, null);
            this.updateVariantInputs(null);
            this.setUnavailable();
          }
        }

        const requestId = ++this.variantRequestId;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          requestId,
          selectedOptionNames,
          shouldRestoreFocus,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedOptionNames),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        requestId,
        selectedOptionNames,
        shouldRestoreFocus = false,
        callback,
      }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.variantSelectors?.setAttribute("aria-busy", "true");

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Product update failed: ${response.status}`);
            }
            return response.text();
          })
          .then((responseText) => {
            if (
              requestId !== this.variantRequestId ||
              !this.selectionMatches(selectedOptionNames)
            ) {
              return;
            }
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            if (
              requestId === this.variantRequestId &&
              shouldRestoreFocus
            ) {
              document.getElementById(targetId)?.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (
              error.name !== "AbortError" &&
              requestId === this.variantRequestId
            ) {
              console.error(error);
            }
          })
          .finally(() => {
            if (requestId === this.variantRequestId) {
              this.variantSelectors?.removeAttribute("aria-busy");
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      getSelectedOptionNames() {
        if (!this.variantSelectors) return [];

        return Array.from(
          this.variantSelectors.querySelectorAll("select, fieldset")
        )
          .map((optionGroup) =>
            optionGroup.matches("select")
              ? optionGroup.value
              : optionGroup.querySelector("input:checked")?.value
          )
          .filter((value) => typeof value === "string");
      }

      getVariantPreview(optionNames) {
        return this.getVariantPreviews().find(
          (preview) =>
            preview.options.length === optionNames.length &&
            preview.options.every((value, index) => value === optionNames[index])
        );
      }

      getVariantPreviews() {
        const source = this.variantSelectors?.querySelector(
          "script[data-variant-previews]"
        );
        if (!source) return [];
        if (this.variantPreviewSource === source) return this.variantPreviewData;

        this.variantPreviewSource = source;
        try {
          const parsed = JSON.parse(source.textContent || "{}");
          this.variantPreviewData = Array.isArray(parsed.variants)
            ? parsed.variants
            : [];
        } catch (error) {
          console.error(error);
          this.variantPreviewData = [];
        }
        return this.variantPreviewData;
      }

      handleVariantKeydown(event) {
        if (
          event.key !== "Enter" ||
          !event.target?.matches?.('input[type="radio"]')
        ) {
          return;
        }

        event.preventDefault();
        event.target.click();
      }

      selectionMatches(optionNames) {
        const currentOptionNames = this.getSelectedOptionNames();

        return (
          currentOptionNames.length === optionNames.length &&
          currentOptionNames.every(
            (optionName, index) => optionName === optionNames[index]
          )
        );
      }

      updateOptionAvailability(optionNames) {
        if (!this.variantSelectors || optionNames.length === 0) return;

        const variants = this.getVariantPreviews();

        if (variants.length === 0) return;

        Array.from(
          this.variantSelectors.querySelectorAll("select, fieldset")
        ).forEach((optionGroup, optionIndex) => {
          const controls = optionGroup.matches("select")
            ? Array.from(optionGroup.options)
            : Array.from(
                optionGroup.querySelectorAll('input[type="radio"]')
              );

          controls.forEach((control) => {
            const candidateOptions = [...optionNames];
            candidateOptions[optionIndex] = control.value;
            const matchingVariant = variants.find(
              (variant) =>
                variant.options.length === candidateOptions.length &&
                variant.options.every(
                  (optionName, index) =>
                    optionName === candidateOptions[index]
                )
            );

            control.classList.toggle(
              "disabled",
              !matchingVariant?.available
            );
          });
        });
      }

      applyVariantPreview(preview, productUrl) {
        const variantId = preview.id;
        const available = preview.available === true;
        const currentPrice = this.querySelector(
          `#price-${this.dataset.section}`
        );

        this.updateURL(productUrl, variantId);
        this.updateVariantInputs(variantId);

        if (currentPrice && !preview.serverPriceOnly) {
          this.updatePreviewPrice(currentPrice, preview);
          currentPrice.classList.remove("hidden");
        }

        this.productForm?.toggleSubmitButton(
          !available,
          available ? undefined : window.variantStrings.soldOut
        );
        this.updatePreviewMedia(preview);
      }

      updatePreviewPrice(currentPrice, preview) {
        const price = currentPrice.querySelector(".price");
        if (!price) return;

        price.classList.toggle("price--sold-out", !preview.available);
        price.classList.toggle("price--on-sale", preview.onSale);

        currentPrice
          .querySelectorAll(
            ".price__regular .price-item--regular, .price__sale .price-item--sale"
          )
          .forEach((element) => {
            element.textContent = preview.price;
          });

        const compareAt = currentPrice.querySelector(
          ".price__compare-at .price-item--regular"
        );
        if (compareAt) compareAt.textContent = preview.compareAtPrice || "";
      }

      updatePreviewMedia(preview) {
        const mediaId = preview.featuredMediaId;
        const mediaGallery = this.querySelector("media-gallery");
        if (!mediaId || !mediaGallery) return;

        const fullMediaId = `${this.dataset.section}-${mediaId}`;
        const existingMedia = Array.from(
          mediaGallery.querySelectorAll("[data-media-id]")
        ).find((media) => media.dataset.mediaId === fullMediaId);

        if (existingMedia) {
          mediaGallery.setActiveMedia?.(fullMediaId, true, false);
          return;
        }

        if (preview.featuredMediaType !== "image") return;
        const imageUrl = preview.featuredImageUrl;
        const currentImage = mediaGallery.querySelector(
          "li.is-active img, li[data-media-id] img"
        );
        if (!imageUrl || !currentImage) return;

        currentImage.srcset = imageUrl;
        currentImage.src = imageUrl;
      }

      variantMatchesSelection(variant, optionNames) {
        return (
          !!variant &&
          variant.options?.length === optionNames.length &&
          variant.options.every(
            (value, index) => value === optionNames[index]
          )
        );
      }

      updateOptionValues(html) {
        const source = html.querySelector("variant-selects");
        const destination = this.variantSelectors;
        if (!source || !destination) return;

        const sourceControls = new Map(
          Array.from(
            source.querySelectorAll("[data-option-value-id]")
          ).map((control) => [control.dataset.optionValueId, control])
        );
        const destinationControls = Array.from(
          destination.querySelectorAll("[data-option-value-id]")
        );

        destinationControls.forEach((control) => {
          const updated = sourceControls.get(control.dataset.optionValueId);
          if (!updated) return;

          control.className = updated.className;
          control.dataset.productUrl = updated.dataset.productUrl || "";

          if (control.matches('input[type="radio"]')) {
            control.checked = updated.checked;
            const currentLabel = destination.querySelector(
              `label[for="${control.id}"]`
            );
            const updatedLabel = source.querySelector(
              `label[for="${updated.id}"]`
            );
            if (currentLabel && updatedLabel) {
              currentLabel.innerHTML = updatedLabel.innerHTML;
            }
          } else if (control.matches("option")) {
            control.selected = updated.selected;
            control.innerHTML = updated.innerHTML;
            ["optionSwatchValue", "optionSwatchFocalPoint"].forEach(
              (attribute) => {
                if (updated.dataset[attribute]) {
                  control.dataset[attribute] = updated.dataset[attribute];
                } else {
                  delete control.dataset[attribute];
                }
              }
            );
          }
        });

        const currentSelectedVariant = destination.querySelector(
          "[data-selected-variant]"
        );
        const updatedSelectedVariant = source.querySelector(
          "[data-selected-variant]"
        );
        if (currentSelectedVariant && updatedSelectedVariant) {
          currentSelectedVariant.textContent = updatedSelectedVariant.textContent;
        }

        const currentSelectedValues = destination.querySelectorAll(
          "[data-selected-value]"
        );
        const updatedSelectedValues = source.querySelectorAll(
          "[data-selected-value]"
        );
        currentSelectedValues.forEach((selectedValue, index) => {
          if (updatedSelectedValues[index]) {
            selectedValue.innerHTML = updatedSelectedValues[index].innerHTML;
          }
        });
      }

      handleUpdateProductInfo(productUrl, selectedOptionNames) {
        return (html) => {
          if (
            !html.querySelector("variant-selects") ||
            !html.getElementById(`price-${this.sectionId}`) ||
            !html.getElementById(`ProductSubmitButton-${this.sectionId}`)
          ) {
            throw new Error("Product update response was incomplete");
          }

          const variant = this.getSelectedVariant(html);
          const preview = this.getVariantPreview(selectedOptionNames);
          if (!this.variantMatchesSelection(variant, selectedOptionNames)) {
            // A null variant is a real unavailable combination only when the
            // local product data also cannot resolve the selected options.
            if (variant || preview) {
              throw new Error("Product update response did not match selection");
            }
            this.updateURL(productUrl, null);
            this.updateVariantInputs(null);
            this.setUnavailable();
            return;
          }

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.syncPackQuantityState();
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.disabled = !variantId;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.quantityRulesAbortController?.abort();
        this.quantityRulesAbortController = new AbortController();
        const requestId = ++this.quantityRulesRequestId;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        )?.classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
          { signal: this.quantityRulesAbortController.signal }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Quantity update failed: ${response.status}`);
            }
            return response.text();
          })
          .then((responseText) => {
            if (
              requestId !== this.quantityRulesRequestId ||
              this.productForm?.variantIdInput?.value !== currentVariantId
            ) {
              return;
            }
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((error) => {
            if (error.name !== "AbortError") console.error(error);
          })
          .finally(() => {
            if (requestId === this.quantityRulesRequestId) {
              this.querySelector(
                ".quantity__rules-cart .loading__spinner"
              )?.classList.add("hidden");
            }
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        if (!quantityFormUpdated || !this.quantityForm) return;
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === ".quantity__label") {
              const updatedAriaLabelledBy =
                updated.getAttribute("aria-labelledby");
              if (updatedAriaLabelledBy) {
                current.setAttribute("aria-labelledby", updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }

        this.setQuantityBoundries();
        this.syncPackQuantityState();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get("related-products-block")) {
class RelatedProductsBlock extends HTMLElement {
  connectedCallback() {
    if (this.variantIdChangedUnsubscriber) return;

    this.sectionId = this.dataset.sectionId;
    this.variantIdChangedUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      ({ data: { sectionId, html } }) => {
        if (sectionId !== this.sectionId) return;
        this.updateHtml(html);
      }
    );
    this.initializeScroller();
  }

  initializeScroller() {
    this.scroller = this.querySelector(".related-products__grid");
    if (!this.scroller) return;

    this.scheduleScrollStateUpdate = () => {
      if (this.scrollStateFrame) return;
      this.scrollStateFrame = requestAnimationFrame(() => {
        this.scrollStateFrame = undefined;
        const maxScroll = Math.max(
          0,
          this.scroller.scrollWidth - this.scroller.clientWidth
        );
        const scrollLeft = Math.max(0, this.scroller.scrollLeft);

        this.classList.toggle("cca-related-scrollable", maxScroll > 2);
        this.classList.toggle("cca-related-at-start", scrollLeft <= 2);
        this.classList.toggle(
          "cca-related-at-end",
          scrollLeft >= maxScroll - 2
        );
      });
    };

    this.scroller.addEventListener("scroll", this.scheduleScrollStateUpdate, {
      passive: true,
    });

    if ("ResizeObserver" in window) {
      this.relatedResizeObserver = new ResizeObserver(
        this.scheduleScrollStateUpdate
      );
      this.relatedResizeObserver.observe(this.scroller);
    }

    this.relatedMutationObserver = new MutationObserver(
      this.scheduleScrollStateUpdate
    );
    this.relatedMutationObserver.observe(this.scroller, {
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-current", "class", "href"],
    });

    this.scheduleScrollStateUpdate();
  }

  updateHtml(html) {
    const updatedBlock = html.getElementById(
      `RelatedProducts-${this.sectionId}`
    );
    if (!updatedBlock) return;

    const updatedLinks = new Map(
      Array.from(
        updatedBlock.querySelectorAll("[data-related-product-id]")
      ).map((link) => [link.dataset.relatedProductId, link])
    );

    this.querySelectorAll("[data-related-product-id]").forEach((link) => {
      const updated = updatedLinks.get(link.dataset.relatedProductId);
      if (!updated) return;

      link.setAttribute("href", updated.getAttribute("href"));
      link.classList.toggle(
        "active_product",
        updated.classList.contains("active_product")
      );

      if (updated.hasAttribute("aria-current")) {
        link.setAttribute("aria-current", updated.getAttribute("aria-current"));
      } else {
        link.removeAttribute("aria-current");
      }
    });

    const currentShape = this.querySelector("[data-current-shape]");
    const updatedShape = updatedBlock.querySelector("[data-current-shape]");
    if (currentShape && updatedShape) {
      currentShape.textContent = updatedShape.textContent;
    }
  }

  disconnectedCallback() {
    this.variantIdChangedUnsubscriber?.();
    this.variantIdChangedUnsubscriber = undefined;
    this.scroller?.removeEventListener(
      "scroll",
      this.scheduleScrollStateUpdate
    );
    this.relatedResizeObserver?.disconnect();
    this.relatedMutationObserver?.disconnect();
    if (this.scrollStateFrame) cancelAnimationFrame(this.scrollStateFrame);
    this.scrollStateFrame = undefined;
  }
}

customElements.define("related-products-block", RelatedProductsBlock);
}
